import { useState, useEffect, useId, useMemo, useRef } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { DateField } from "@/components/DateField";
import { PercentageInput } from "@/components/PercentageInput";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import Link from "@mui/material/Link";
import MaterialSymbol from "@/components/MaterialSymbol";
import AiSuggestPanel, { type AiApplyPayload } from "@/components/AiSuggestPanel";
import { EolLinkDialog } from "@/components/EolLinkSection";
import VendorField from "@/components/VendorField";
import { hasTypePermission } from "@/components/RequirePermission";
import { useAuthContext } from "@/hooks/AuthContext";
import CardPicker, { type CardOption } from "@/components/CardPicker";
import TagPicker from "@/components/TagPicker";
import { useMetamodel } from "@/hooks/useMetamodel";
import {
  useTypeLabel,
  useFieldLabel,
  useOptionLabel,
  useSubtypeLabel,
} from "@/hooks/useResolveLabel";
import { useAiStatus, aiSuggestEnabledFor } from "@/hooks/useAiStatus";
import { useAbortableEffect, useLatestRequest } from "@/hooks/useLatestRequest";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { api, ApiError } from "@/api/client";
import { readableTextColor } from "@/lib/color";
import { isEolType } from "@/lib/eol";
import type {
  FieldDef,
  EolProductMatch,
  AiSuggestResponse,
  TagGroup,
  SiblingNameConflictDetail,
} from "@/types";

const VENDOR_ELIGIBLE_TYPES = ["Application", "ITComponent"];

interface Props {
  open: boolean;
  onClose: () => void;
  onCreate: (data: {
    type: string;
    subtype?: string;
    name: string;
    description?: string;
    parent_id?: string;
    attributes?: Record<string, unknown>;
    lifecycle?: Record<string, string>;
  }) => Promise<string>;
  initialType?: string;
  // Preset the subtype / seed attribute values for the initial type. They
  // apply while the selected type IS the initial type — picking a different
  // type clears them like any other dependent field, and switching back
  // re-applies them. Callers use these to open the dialog pre-configured
  // (e.g. "new Branch" = Organization + subtype preset).
  initialSubtype?: string;
  initialAttributes?: Record<string, unknown>;
}


export default function CreateCardDialog({
  open,
  onClose,
  onCreate,
  initialType,
  initialSubtype,
  initialAttributes,
}: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation(["cards", "common"]);
  const { types, relationTypes } = useMetamodel();
  const { user } = useAuthContext();
  const typeLabel = useTypeLabel();
  const fieldLabel = useFieldLabel();
  const optLabel = useOptionLabel();
  const stLabel = useSubtypeLabel();

  // A card type may deny this role `inventory.create` (discussion #1068), so
  // the picker offers only the types the user may actually create.
  const creatableTypes = useMemo(
    () =>
      types.filter(
        (ct) => !ct.is_hidden && hasTypePermission(user, "inventory.create", ct.key)
      ),
    [types, user]
  );

  const [selectedType, setSelectedType] = useState(initialType || "");
  // Names each picker's combobox after its label: the type's, the subtype's
  // and every required select field's (keyed by the field).
  const typeLabelId = useId();
  const subtypeLabelId = `${typeLabelId}-subtype`;
  const fieldLabelId = (key: string) => `${typeLabelId}-field-${key}`;
  /** `initialType` is supplied by the caller (the inventory's selected type,
   *  a diagram, a deep link) and may be one this role cannot create — so the
   *  submit button is gated on the *selected* type, not just on the list. */
  const typeAllowed = hasTypePermission(user, "inventory.create", selectedType || null);
  const [subtype, setSubtype] = useState(initialType ? initialSubtype || "" : "");
  const [parentCard, setParentCard] = useState<CardOption | null>(null);
  const [name, setName] = useState("");
  // Field-level error for the Name input — populated when the backend
  // returns 409 on a sibling-name collision. Cleared when the user types.
  // Structured details additionally carry the existing card's id so the
  // helper text can link straight to it (#927).
  const [nameConflict, setNameConflict] = useState<{
    message: string;
    existingCardId?: string;
    existingCardName?: string;
    typeKey?: string;
  } | null>(null);
  const [description, setDescription] = useState("");
  const [attributes, setAttributes] = useState<Record<string, unknown>>(() =>
    initialType ? { ...(initialAttributes || {}) } : {},
  );
  // Ref, not an effect dep: callers routinely pass an inline object literal,
  // whose identity changes every render and would loop the reset effect.
  const initialAttributesRef = useRef(initialAttributes);
  initialAttributesRef.current = initialAttributes;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [eolProduct, setEolProduct] = useState("");
  const [eolCycle, setEolCycle] = useState("");
  const [eolDialogOpen, setEolDialogOpen] = useState(false);

  // Auto-search EOL state
  const [eolSuggestions, setEolSuggestions] = useState<EolProductMatch[]>([]);
  const [eolSearching, setEolSearching] = useState(false);
  const [eolAutoSearchDone, setEolAutoSearchDone] = useState(false);
  // The (trimmed) name the finished search was for — its verdict says
  // nothing about a name typed since.
  const [eolSearchedName, setEolSearchedName] = useState("");
  // Why that search failed ("" when it did not) — a failure is not "no match".
  const [eolSearchError, setEolSearchError] = useState("");

  // AI suggestion state
  const { aiStatus } = useAiStatus();
  const [aiResponse, setAiResponse] = useState<AiSuggestResponse | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  // A suggestion belongs to the session that asked for it: closing or
  // reopening the dialog supersedes one still in flight.
  const aiRequest = useLatestRequest();
  useEffect(() => {
    aiRequest.cancel();
  }, [open, aiRequest]);

  // Tag picker state
  const [tagGroups, setTagGroups] = useState<TagGroup[]>([]);
  // Why the last load failed ("" when it was not an Error); null when it did not.
  const [tagGroupsError, setTagGroupsError] = useState<string | null>(null);
  const [tagIds, setTagIds] = useState<string[]>([]);

  // Provider linkage staged in VendorField — `fsId` doesn't exist yet during
  // create, so the relation is posted after the card is saved.
  const [pendingProvider, setPendingProvider] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const typeConfig = useMemo(
    () => types.find((t) => t.key === selectedType),
    [types, selectedType],
  );

  // Localized duplicate-name message. When the 409 detail is structured we
  // rebuild the sentence in the user's locale (entity-aware type label, no
  // raw UUID); otherwise we fall back to the backend's English prose.
  const conflictMessage = useMemo(() => {
    if (!nameConflict) return "";
    if (!nameConflict.existingCardName) return nameConflict.message;
    const conflictType = types.find((tt) => tt.key === nameConflict.typeKey);
    return t("create.duplicate.message", {
      type: conflictType ? typeLabel(conflictType) : nameConflict.typeKey,
      name: nameConflict.existingCardName,
    });
  }, [nameConflict, types, typeLabel, t]);

  const hasSubtypes = !!(typeConfig?.subtypes && typeConfig.subtypes.length > 0);
  const hasHierarchy = !!typeConfig?.has_hierarchy;
  const isEolEligible = isEolType(selectedType);

  // Determine hidden fields for the selected subtype
  const hiddenFieldKeys = useMemo(() => {
    if (!subtype || !typeConfig?.subtypes) return new Set<string>();
    const st = typeConfig.subtypes.find((s) => s.key === subtype);
    return new Set(st?.hidden_fields ?? []);
  }, [subtype, typeConfig]);

  // Collect all required fields across all sections for the selected type
  const requiredFields = useMemo(() => {
    if (!typeConfig) return [];
    const fields: (FieldDef & { sectionName: string })[] = [];
    for (const section of typeConfig.fields_schema) {
      for (const field of section.fields) {
        if (field.required && !hiddenFieldKeys.has(field.key)) {
          fields.push({ ...field, sectionName: section.section });
        }
      }
    }
    return fields;
  }, [typeConfig, hiddenFieldKeys]);

  // Fetch tag groups when the dialog opens. A failed reload keeps the groups
  // already held and says so, rather than silently dropping the picker.
  useEffect(() => {
    if (!open) return;
    setTagGroupsError(null);
    api
      .get<TagGroup[]>("/tag-groups")
      .then(setTagGroups)
      .catch((err: unknown) => setTagGroupsError(err instanceof Error ? err.message : ""));
  }, [open]);

  // Reset dependent fields when type changes. The initial presets survive
  // while the selected type is still the initial one (the open-effect's
  // setSelectedType lands here too, and must not wipe them).
  useEffect(() => {
    const presetsApply = Boolean(selectedType) && selectedType === initialType;
    setSubtype(presetsApply ? initialSubtype || "" : "");
    setParentCard(null);
    setAttributes(presetsApply ? { ...(initialAttributesRef.current || {}) } : {});
    setError("");
    setEolProduct("");
    setEolCycle("");
    setEolSuggestions([]);
    setEolAutoSearchDone(false);
    // A suggestion still in flight was asked for the previous type.
    aiRequest.cancel();
    setAiLoading(false);
    setAiResponse(null);
    setAiError("");
    setTagIds([]);
    setPendingProvider(null);
  }, [selectedType, initialType, initialSubtype, aiRequest]);

  // So was one still in flight when the name or the subtype changed: it was
  // asked for the card as it was then (the name as sent, i.e. trimmed).
  const askedName = name.trim();
  useEffect(() => {
    aiRequest.cancel();
    setAiLoading(false);
  }, [askedName, subtype, aiRequest]);

  // Set initial type when dialog opens
  useEffect(() => {
    if (open && initialType) {
      setSelectedType(initialType);
    }
  }, [open, initialType]);

  // Reset the form as the dialog opens — back to the presets, so reopening
  // with the same initial type starts pre-seeded again. Not as it closes:
  // that blanked the form while it was still fading out. Done while rendering
  // (state adjusted on a prop change) rather than in an effect, so neither the
  // first frame nor any effect sees the previous session; and only on the
  // open transition, so a new initial type while open does not wipe what was
  // typed (the effect above follows it).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setSelectedType(initialType || "");
      setSubtype(initialType ? initialSubtype || "" : "");
      setParentCard(null);
      setName("");
      setNameConflict(null);
      setDescription("");
      setAttributes(initialType ? { ...(initialAttributes || {}) } : {});
      setLoading(false);
      setError("");
      setEolProduct("");
      setEolCycle("");
      setEolDialogOpen(false);
      setEolSuggestions([]);
      setEolAutoSearchDone(false);
      setAiResponse(null);
      setAiLoading(false);
      setAiError("");
      setTagIds([]);
      setPendingProvider(null);
    }
  }

  // Auto-search EOL when name changes (debounced). Cancelling the timer never
  // cancelled a dispatched request, so suggestions for a half-typed name could
  // land last and replace the ones for the finished name (#882).
  const [debouncedName] = useDebouncedValue(name, 600);
  // Keyed on whether a product AND cycle are linked, not on the product: a
  // picked suggestion sets the product alone, and must not search again.
  const eolLinked = Boolean(eolProduct && eolCycle);
  // The live name, read by the search without re-running it per keystroke.
  const nameRef = useRef(name);
  nameRef.current = name;
  useAbortableEffect(
    async ({ signal, isCurrent }) => {
      // Closed: nothing to search for — and re-running here supersedes a
      // search still in flight, so its answer cannot land in the next session.
      if (!open) return;
      const trimmed = debouncedName.trim();
      if (!isEolEligible || !trimmed || trimmed.length < 2) {
        setEolSuggestions([]);
        setEolAutoSearchDone(false);
        setEolSearching(false);
        return;
      }
      // Don't auto-search if already linked
      if (eolLinked) return;
      // The name has moved on since it settled (the form was just reset on
      // open): don't search the old one — the debounce brings the new one.
      if (debouncedName !== nameRef.current) {
        setEolSearching(false);
        return;
      }

      setEolSearching(true);
      try {
        const results = await api.get<EolProductMatch[]>(
          `/eol/products/fuzzy?search=${encodeURIComponent(trimmed)}&limit=5`,
          { signal },
        );
        if (!isCurrent()) return;
        setEolSuggestions(results);
        setEolSearchedName(trimmed);
        setEolSearchError("");
        setEolAutoSearchDone(true);
      } catch (err) {
        if (!isCurrent()) return;
        setEolSuggestions([]);
        setEolSearchedName(trimmed);
        setEolSearchError(err instanceof Error ? err.message : t("eol.errors.fetchFailed"));
        setEolAutoSearchDone(true);
      } finally {
        if (isCurrent()) setEolSearching(false);
      }
    },
    [debouncedName, isEolEligible, eolLinked, open],
  );

  const setAttr = (key: string, value: unknown) => {
    setAttributes((prev) => ({ ...prev, [key]: value }));
  };

  // Whether AI suggest button should be shown for the current type
  const aiEnabled = aiSuggestEnabledFor(aiStatus, selectedType);

  const handleAiSuggest = async () => {
    if (!selectedType || !name.trim()) return;
    await aiRequest.run(async ({ isCurrent }) => {
      setAiLoading(true);
      setAiError("");
      setAiResponse(null);
      try {
        const res = await api.post<AiSuggestResponse>("/ai/suggest", {
          type_key: selectedType,
          subtype: subtype || undefined,
          name: name.trim(),
        });
        if (!isCurrent()) return;
        setAiResponse(res);
      } catch (err: unknown) {
        if (!isCurrent()) return;
        setAiError(err instanceof Error ? err.message : t("common:errors.generic"));
      } finally {
        if (isCurrent()) setAiLoading(false);
      }
    });
  };

  const handleAiApply = (payload: AiApplyPayload) => {
    if (payload.description) {
      setDescription(payload.description);
    }
    if (payload.fields) {
      setAttributes((prev) => ({ ...prev, ...payload.fields }));
    }
    setAiResponse(null);
  };

  const handleSelectSuggestion = (productName: string) => {
    setEolProduct(productName);
    setEolCycle(""); // User still needs to pick a cycle
    setEolDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!selectedType || !name.trim()) return;
    setLoading(true);
    setError("");
    setNameConflict(null);
    try {
      const finalAttrs = { ...attributes };
      if (eolProduct && eolCycle) {
        finalAttrs.eol_product = eolProduct;
        finalAttrs.eol_cycle = eolCycle;
      }

      // Defensive: never persist an orphan vendor text attribute. Provider
      // linkage is owned by the relation created below.
      delete finalAttrs.vendor;

      const newId = await onCreate({
        type: selectedType,
        subtype: subtype || undefined,
        name: name.trim(),
        description: description.trim() || undefined,
        parent_id: parentCard?.id || undefined,
        attributes:
          Object.keys(finalAttrs).length > 0 ? finalAttrs : undefined,
      });
      if (tagIds.length > 0) {
        try {
          await api.post(`/cards/${newId}/tags`, tagIds);
        } catch {
          // Card was created successfully; tag-assignment failure is non-fatal.
        }
      }
      if (pendingProvider) {
        // Several relation types may connect Provider to this card type; pick
        // the same one VendorField shows — lowest sort_order, then key.
        const relType = relationTypes
          .filter(
            (r) =>
              (r.source_type_key === "Provider" &&
                r.target_type_key === selectedType) ||
              (r.target_type_key === "Provider" &&
                r.source_type_key === selectedType),
          )
          .sort(
            (a, b) =>
              (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.key.localeCompare(b.key),
          )[0];
        if (relType) {
          const providerIsSource = relType.source_type_key === "Provider";
          try {
            await api.post("/relations", {
              type: relType.key,
              source_id: providerIsSource ? pendingProvider.id : newId,
              target_id: providerIsSource ? newId : pendingProvider.id,
            });
          } catch {
            // Card is created; failing to link the Provider is non-fatal —
            // the user can re-link from the card detail page.
          }
        }
      }
      onClose();
      navigate(`/cards/${newId}`);
    } catch (err: unknown) {
      // Surface the sibling-name collision (HTTP 409) on the Name field
      // directly — it's a validation error on a single input, not a
      // dialog-wide failure. Structured details carry the existing card's
      // id/name so we can render a localized message plus a link to it;
      // anything else falls back to the raw detail string.
      if (err instanceof ApiError && err.status === 409) {
        const d = err.detail as Partial<SiblingNameConflictDetail> | string | null;
        if (d && typeof d === "object" && d.code === "sibling_name_conflict") {
          setNameConflict({
            message: d.message ?? err.message,
            existingCardId: d.existing_card_id,
            existingCardName: d.existing_card_name,
            typeKey: d.type_key,
          });
        } else {
          setNameConflict({ message: typeof d === "string" ? d : err.message });
        }
        return;
      }
      const message =
        err instanceof Error ? err.message : t("create.failed");
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field: FieldDef) => {
    switch (field.type) {
      case "single_select":
        return (
          <FormControl fullWidth key={field.key} required={field.required} sx={{ mb: 2 }}>
            <InputLabel id={fieldLabelId(field.key)}>{fieldLabel(field)}</InputLabel>
            <Select
              labelId={fieldLabelId(field.key)}
              value={(attributes[field.key] as string) ?? ""}
              label={fieldLabel(field)}
              onChange={(e) => setAttr(field.key, e.target.value || undefined)}
            >
              <MenuItem value="">
                <em>{t("common:labels.none")}</em>
              </MenuItem>
              {field.options?.map((opt) => (
                <MenuItem key={opt.key} value={opt.key}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {opt.color && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: opt.color,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {optLabel(opt)}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "multiple_select": {
        const arrVal: string[] = Array.isArray(attributes[field.key])
          ? (attributes[field.key] as unknown[]).filter(
              (v): v is string => typeof v === "string",
            )
          : [];
        const labelText = fieldLabel(field);
        return (
          <FormControl fullWidth key={field.key} required={field.required} sx={{ mb: 2 }}>
            <InputLabel id={fieldLabelId(field.key)}>{labelText}</InputLabel>
            <Select
              labelId={fieldLabelId(field.key)}
              multiple
              value={arrVal}
              label={labelText}
              onChange={(e) => {
                const v = e.target.value;
                const arr = typeof v === "string" ? v.split(",") : v;
                setAttr(field.key, arr.length > 0 ? arr : undefined);
              }}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(selected as string[]).map((key) => {
                    const opt = field.options?.find((o) => o.key === key);
                    return (
                      <Chip
                        key={key}
                        size="small"
                        label={opt ? optLabel(opt) : key}
                        onMouseDown={(e) => e.stopPropagation()}
                        onDelete={() => {
                          const next = arrVal.filter((v) => v !== key);
                          setAttr(field.key, next.length > 0 ? next : undefined);
                        }}
                        sx={{
                          height: 22,
                          ...(opt?.color
                            ? {
                                bgcolor: opt.color,
                                color: readableTextColor(opt.color),
                                "& .MuiChip-deleteIcon": {
                                  color: readableTextColor(opt.color),
                                  opacity: 0.85,
                                },
                              }
                            : {}),
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {field.options?.map((opt) => (
                <MenuItem key={opt.key} value={opt.key}>
                  <Checkbox
                    size="small"
                    checked={arrVal.includes(opt.key)}
                    sx={{ p: 0.5, mr: 1 }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {opt.color && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: opt.color,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {optLabel(opt)}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      case "percentage":
        return (
          <PercentageInput
            key={field.key}
            fullWidth
            size="medium"
            required={field.required}
            label={fieldLabel(field)}
            value={attributes[field.key]}
            onChange={(v) => setAttr(field.key, v)}
            sx={{ mb: 2 }}
          />
        );

      case "cost":
      case "number":
        return (
          <TextField
            key={field.key}
            fullWidth
            required={field.required}
            label={fieldLabel(field)}
            type="number"
            value={attributes[field.key] ?? ""}
            onChange={(e) =>
              setAttr(
                field.key,
                e.target.value ? Number(e.target.value) : undefined,
              )
            }
            sx={{ mb: 2 }}
          />
        );

      case "boolean":
        return (
          <FormControlLabel
            key={field.key}
            control={
              <Switch
                checked={!!attributes[field.key]}
                onChange={(e) => setAttr(field.key, e.target.checked)}
              />
            }
            label={fieldLabel(field)}
            sx={{ mb: 1, display: "block" }}
          />
        );

      case "date":
        return (
          <DateField
            key={field.key}
            fullWidth
            required={field.required}
            label={fieldLabel(field)}
            value={(attributes[field.key] as string) ?? ""}
            onChange={(v) => setAttr(field.key, v || undefined)}
            sx={{ mb: 2 }}
          />
        );

      case "text":
      default:
        return (
          <TextField
            key={field.key}
            fullWidth
            required={field.required}
            label={fieldLabel(field)}
            value={(attributes[field.key] as string) ?? ""}
            onChange={(e) => setAttr(field.key, e.target.value || undefined)}
            sx={{ mb: 2 }}
          />
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { maxWidth: 560, width: "100%" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        {t("create.title")}
        <IconButton
          aria-label={t("common:actions.close")}
          onClick={onClose}
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <MaterialSymbol icon="close" size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* Type selector */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id={typeLabelId}>{t("common:labels.type")}</InputLabel>
          <Select
            labelId={typeLabelId}
            value={selectedType}
            label={t("common:labels.type")}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            {creatableTypes.map((t) => (
              <MenuItem key={t.key} value={t.key}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: t.color,
                      flexShrink: 0,
                    }}
                  />
                  <MaterialSymbol icon={t.icon} size={20} color={t.color} />
                  {typeLabel(t)}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Subtype selector */}
        {hasSubtypes && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id={subtypeLabelId}>{t("common:labels.subtype")}</InputLabel>
            <Select
              labelId={subtypeLabelId}
              value={subtype}
              label={t("common:labels.subtype")}
              onChange={(e) => setSubtype(e.target.value)}
            >
              <MenuItem value="">
                <em>{t("common:labels.none")}</em>
              </MenuItem>
              {typeConfig!.subtypes!.map((st) => (
                <MenuItem key={st.key} value={st.key}>
                  {stLabel(st)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Parent selector */}
        {hasHierarchy && (
          <CardPicker
            sx={{ mb: 2 }}
            size="medium"
            fullWidth
            types={selectedType}
            value={parentCard}
            onChange={setParentCard}
            enabled={hasHierarchy && !!selectedType}
            label={t("common:labels.parent")}
          />
        )}

        {/* Name */}
        <TextField
          fullWidth
          label={t("common:labels.name")}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (nameConflict) setNameConflict(null);
          }}
          required
          error={!!nameConflict}
          helperText={
            nameConflict ? (
              // helperText renders inside a <p>, so only inline elements
              // are valid here — MUI Link renders an <a>, which is fine.
              <>
                {conflictMessage}{" "}
                {nameConflict.existingCardId && (
                  <Link
                    href={`/cards/${nameConflict.existingCardId}`}
                    onClick={(e) => {
                      // Let modified clicks open a new tab via the href.
                      if (e.ctrlKey || e.metaKey || e.shiftKey) return;
                      e.preventDefault();
                      onClose();
                      navigate(`/cards/${nameConflict.existingCardId}`);
                    }}
                  >
                    {t("create.duplicate.view")}
                  </Link>
                )}
              </>
            ) : undefined
          }
          sx={{ mb: 2 }}
        />

        {/* Description */}
        <TextField
          fullWidth
          label={t("common:labels.description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={3}
          sx={{ mb: 2 }}
        />

        {/* Tags */}
        {selectedType && tagGroupsError !== null && (
          // Stryker disable next-line ObjectLiteral: spacing is presentation
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("create.tagsLoadFailed", {
              error: tagGroupsError || t("common:errors.generic"),
            })}
          </Alert>
        )}
        {selectedType && tagGroups.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <TagPicker
              groups={tagGroups}
              value={tagIds}
              onChange={setTagIds}
              typeKey={selectedType}
            />
          </Box>
        )}

        {/* AI Suggest button */}
        {aiEnabled && name.trim().length >= 2 && !aiResponse && !aiLoading && (
          <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-start" }}>
            {/* describeChild: the tooltip describes the button, and the
                visible label stays its accessible name (WCAG 2.5.3). */}
            <Tooltip title={t("common:ai.buttonTooltip")} describeChild>
              <Button
                size="small"
                variant="outlined"
                onClick={handleAiSuggest}
                startIcon={<MaterialSymbol icon="auto_awesome" size={16} />}
                sx={{ textTransform: "none" }}
              >
                {t("common:ai.suggestButton")}
              </Button>
            </Tooltip>
          </Box>
        )}

        {/* AI Suggestion Panel */}
        {(aiLoading || aiError || aiResponse) && (
          <AiSuggestPanel
            response={aiResponse}
            loading={aiLoading}
            error={aiError}
            onApply={handleAiApply}
            onDismiss={() => {
              setAiResponse(null);
              setAiError("");
              setAiLoading(false);
            }}
            fieldsSchema={typeConfig?.fields_schema}
          />
        )}

        {/* EOL section - below description, with auto-search */}
        {isEolEligible && (
          <Box
            sx={{
              mt: 0,
              mb: 2,
              p: 1.5,
              border: "1px dashed",
              borderColor: eolProduct ? "success.main" : "divider",
              borderRadius: 1,
              ...(eolProduct
                ? { bgcolor: "rgba(76, 175, 80, 0.04)" }
                : {}),
            }}
          >
            {eolProduct && eolCycle ? (
              /* Already linked */
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <MaterialSymbol
                  icon="check_circle"
                  size={18}
                  color="#4caf50"
                />
                <Typography variant="body2">
                  {t("eol.linkedTo")}{" "}
                  <strong>
                    {eolProduct} {eolCycle}
                  </strong>
                </Typography>
                <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
                  <Button
                    size="small"
                    onClick={() => setEolDialogOpen(true)}
                  >
                    {t("common:actions.change")}
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setEolProduct("");
                      setEolCycle("");
                      setEolAutoSearchDone(false);
                    }}
                  >
                    {t("common:actions.remove")}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: eolSearching || (eolAutoSearchDone && eolSuggestions.length > 0) ? 1.5 : 0,
                  }}
                >
                  <MaterialSymbol icon="update" size={18} color="#666" />
                  <Typography variant="body2" color="text.secondary">
                    {t("eol.tracking")}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ ml: "auto" }}
                    onClick={() => setEolDialogOpen(true)}
                  >
                    {t("eol.manualSearch")}
                  </Button>
                </Box>

                {/* Auto-search loading */}
                {eolSearching && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress sx={{ mb: 0.5, borderRadius: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      {t("eol.searching", { name: name.trim() })}
                    </Typography>
                  </Box>
                )}

                {/* Auto-search suggestions */}
                {!eolSearching && eolAutoSearchDone && eolSuggestions.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      {t("eol.suggestedMatches")}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {eolSuggestions.map((s) => (
                        <Chip
                          key={s.name}
                          label={s.name}
                          size="small"
                          variant="outlined"
                          onClick={() => handleSelectSuggestion(s.name)}
                          icon={<MaterialSymbol icon="link" size={14} />}
                          sx={{
                            cursor: "pointer",
                            borderColor: s.score >= 0.7 ? "success.main" : "divider",
                            fontWeight: s.score >= 0.7 ? 600 : 400,
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* The search failed: not the same as finding nothing */}
                {!eolSearching && eolAutoSearchDone && eolSearchError && eolSearchedName === name.trim() && (
                  // Stryker disable next-line ObjectLiteral: spacing is presentation
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {eolSearchError}
                  </Alert>
                )}

                {/* No matches found */}
                {!eolSearching && eolAutoSearchDone && !eolSearchError && eolSuggestions.length === 0 && eolSearchedName === name.trim() && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    {t("eol.noMatches")}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Provider linker for eligible types — posts the relation after
            the card is created (handleSubmit), since fsId doesn't exist yet. */}
        {VENDOR_ELIGIBLE_TYPES.includes(selectedType) && (
          <Box sx={{ mb: 2 }}>
            <VendorField
              value={pendingProvider?.name ?? ""}
              onChange={() => {
                /* input value is owned by VendorField in the create flow;
                   no `attributes.vendor` is stored — the relation is the
                   source of truth. */
              }}
              onProviderSelected={setPendingProvider}
              cardTypeKey={selectedType}
              size="small"
            />
          </Box>
        )}

        {/* Required fields from schema */}
        {requiredFields.length > 0 && requiredFields.map((f) => renderField(f))}

        {/* EOL picker dialog */}
        <EolLinkDialog
          open={eolDialogOpen}
          onClose={() => setEolDialogOpen(false)}
          onLink={(product, cycle) => {
            setEolProduct(product);
            setEolCycle(cycle);
            setEolSuggestions([]);
          }}
          initialProduct={eolProduct || undefined}
          cardName={name.trim() || undefined}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">
          {t("common:actions.cancel")}
        </Button>
        <Tooltip title={typeAllowed ? "" : t("create.noTypePermission")}>
          <span>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!selectedType || !typeAllowed || !name.trim() || loading}
              startIcon={
                loading ? <CircularProgress size={18} color="inherit" /> : undefined
              }
            >
              {t("common:actions.create")}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
